import React, { memo, useMemo, useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Context from 'managers/Context';
import { useDetailFieldDynamism } from 'containers/components/EntityDetail/hooks/useDetailFieldDynamism';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { WidgetLayout, WidgetHeaderLayout, WidgetContentLayout, WidgetEmpty } from '../Layouts';
import { logEvent } from 'utils/tracking';

import SizeComponent from 'components/SizeComponent';
import Header from './Header';
import Content from './Content';
import { ExtraFieldsIcon } from 'components/SvgIcons';
import { getLiteral } from 'utils/getLiteral';
import colors from 'constants/colors';

import './styles.scss';

const ExtraFieldsWidget = ({ entityType, entityId, entity, setSize, config }) => {
    const [sectionSelected, setSectionSelected] = useState(0);
    const [rawSections, setRawSections] = useState([]);
    const [loading, setLoading] = useState(false);

    const { filterExtraFields, hasShadowCrud } = useDetailFieldDynamism();

    const sections = useMemo(() => {
        return rawSections
            .map((section) => {
                const visibleFields = hasShadowCrud
                    ? filterExtraFields(section.data, {
                          entityExtraFieldName: entityType?.extraFieldName,
                      })
                    : (section.data || []).filter(
                          (field) => !getBackendBoolean(field.hiddenByDynamism),
                      );

                return {
                    ...section,
                    data: visibleFields,
                };
            })
            .filter((section) => section.data.length > 0);
    }, [entityType?.extraFieldName, filterExtraFields, hasShadowCrud, rawSections]);

    useEffect(() => {
        setSize(
            {
                height: sections.length === 0 ? 120 : config.height,
            },
            true,
        );
    }, [config.height, sections.length, setSize]);

    useEffect(() => {
        if (!entityType) return;

        setLoading(true);

        const manager = Context.entityManager.getEntitiesManager(entityType);
        if (manager && manager.getExtraFieldsByEntity) {
            manager.getExtraFieldsByEntity(entityId).then((data) => {
                setRawSections(data || []);
                setLoading(false);
            });
        } else if (entity?.extraFields?.length) {
            setRawSections(entity.extraFields);
            setLoading(false);
        } else {
            setRawSections([]);
            setLoading(false);
            setSize({
                height: 120,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity?.extraFields, entityId, entityType]);

    const onClickSection = useCallback(
        (event, section) => {
            logEvent({
                event: entityType?.trueName,
                submodule: 'extraFieldsWidget',
                functionality: 'showExtraFields',
            });
            setSectionSelected(section);
        },
        [entityType?.trueName],
    );

    const content = useMemo(() => {
        if (sections.length === 0) return null;
        return sections[sectionSelected];
    }, [sections, sectionSelected]);

    const isEmpty = sections.length === 0;

    return (
        <WidgetLayout
            type="tabs"
            className="fm-widget-extra-fields"
            data={entityId && entityType}
            loading={loading}
        >
            <WidgetHeaderLayout>
                <Header
                    sections={sections}
                    onChange={onClickSection}
                    tabControlled={sectionSelected}
                />
            </WidgetHeaderLayout>
            <WidgetContentLayout>
                {!isEmpty && <Content entityType={entityType} section={content} data={entity} />}
                {isEmpty && (
                    <SizeComponent setSize={setSize}>
                        <WidgetEmpty
                            icon={<ExtraFieldsIcon color={colors.grey[100]} />}
                            text={getLiteral('error_noextrafieldsfound')}
                        />
                    </SizeComponent>
                )}
            </WidgetContentLayout>
        </WidgetLayout>
    );
};

ExtraFieldsWidget.propTypes = {
    entityType: PropTypes.object.isRequired,
    entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    entity: PropTypes.object,
    setSize: PropTypes.func.isRequired,
    config: PropTypes.shape({
        height: PropTypes.number,
    }),
};

export default memo(ExtraFieldsWidget);
